import { gsap, ScrollTrigger } from "@gsap";

/**
 * Component: Shape
 *
 * Elke vorm heeft een eigen animatie. Pas hieronder alleen SHAPES aan:
 * de sleutel is het nummer van het bestand (shape-3.svg -> 3).
 * Wat je niet opgeeft, komt uit DEFAULTS.
 *
 * Starten: per layout, via de PHP-config van de component:
 *   Render::component('shape', 'shape', ['trigger' => 'scroll']);   // start zodra de vorm in beeld komt
 *   Render::component('shape', 'shape');                           // 'auto' (standaard): start direct
 * Bij 'scroll' is de vorm zelf de trigger, met start "top 60%" (SCROLL_START).
 *
 * Opties per vorm:
 *   preset    Hoe de paden beginnen (zie PRESETS hieronder).
 *   distance  Afstand in SVG-eenheden (viewBox is 511 breed) voor radial / slide-*.
 *   rotation  Graden die de hele vorm draait. Gebeurt eerst, daarna vliegen de paden binnen.
 *   duration  Duur per pad in seconden.
 *   stagger   Vertraging tussen paden in seconden.
 *   delay     Wachttijd voor de hele animatie.
 *   ease      GSAP ease voor de paden.
 *   order     "start" (DOM-volgorde), "end" (omgekeerd), "center", "edges" of "random".
 *   paths     Overschrijf losse paden op index (0 = eerste <path>), bijvoorbeeld
 *             { 2: { x: 60, y: -40 }, 5: { preset: "fade" } }.
 *             Per pad kan alles uit "from" (x, y, scale, rotation, opacity) en "preset".
 */

const SCROLL_START = "top 60%";

const DEFAULTS = {
    preset: "radial",
    distance: 80,
    rotation: 0,
    duration: 1.5,
    stagger: 0.15,
    delay: 0.5,
    ease: "power3.out",
    order: "start",
    paths: {},
};

const SHAPES = {
    1: { rotation: -10 },
    2: { rotation: -10 },
    3: { rotation: -10, stagger: 0.1 },
    4: { rotation: -10 },
    5: { rotation: -10 },
    6: { rotation: -10, stagger: 0.08 },
    7: { rotation: -10 },
    8: { rotation: -10, stagger: 0.1 },
    9: { rotation: -10 },
    10: { rotation: -10 },
};

/**
 * Beginstand van een pad. Krijgt de afstand en de richting (dx, dy, lengte 1)
 * van het midden van de vorm naar het midden van het pad, en de index van het pad.
 * Geeft de "from"-waarden terug.
 */
const PRESETS = {
    // Vanuit het midden van de vorm naar buiten, werkt bij elk aantal paden.
    radial: ({ distance, dx, dy }) => ({ x: dx * distance, y: dy * distance }),
    // Vanuit de vier hoeken, om en om (zoals de eerste hero).
    corners: ({ distance, index }) => {
        const c = [[1, -1], [-1, -1], [-1, 1], [1, 1]][index % 4];
        return { x: c[0] * distance, y: c[1] * distance };
    },
    "slide-up": ({ distance }) => ({ y: distance }),
    "slide-down": ({ distance }) => ({ y: -distance }),
    "slide-left": ({ distance }) => ({ x: distance }),
    "slide-right": ({ distance }) => ({ x: -distance }),
    scale: () => ({ scale: 0, transformOrigin: "50% 50%" }),
    fade: () => ({}),
};

const getShapeConfig = (shape) => {
    const id = parseInt(shape.dataset.shape, 10);
    const config = { ...DEFAULTS, ...SHAPES[id] };

    // Een rotatie die de layout meegeeft (data-rotation) wint van de lijst hierboven.
    if (shape.dataset.rotation) config.rotation = parseFloat(shape.dataset.rotation);

    config.scroll = shape.dataset.trigger === "scroll";

    return config;
};

const getFromState = (segment, index, svg, config) => {
    const override = config.paths[index] || {};
    const preset = PRESETS[override.preset || config.preset] || PRESETS.radial;

    // Midden van het pad t.o.v. het midden van de vorm, als richting met lengte 1.
    const box = segment.getBBox();
    const view = svg.viewBox.baseVal;
    const vx = box.x + box.width / 2 - (view.x + view.width / 2);
    const vy = box.y + box.height / 2 - (view.y + view.height / 2);
    const length = Math.hypot(vx, vy) || 1;

    const { preset: _preset, ...extra } = override;

    return {
        ...preset({ distance: config.distance, dx: vx / length, dy: vy / length, index }),
        ...extra,
        opacity: 0,
    };
};

const initShapes = () => {
    document.querySelectorAll(".pk-shape").forEach((shape) => {
        const svg = shape.querySelector("svg");
        const segments = svg ? [...svg.querySelectorAll("path")] : [];

        if (!svg || !segments.length) return;

        const config = getShapeConfig(shape);
        const motion = gsap.matchMedia();

        motion.add(
            {
                reduce: "(prefers-reduced-motion: reduce)",
                animate: "(prefers-reduced-motion: no-preference)",
            },
            (context) => {
                if (context.conditions.reduce) {
                    gsap.set(segments, { opacity: 1 });
                    return;
                }

                // Bij scroll start de timeline eenmalig zodra de vorm de start-lijn passeert; de delay vervalt dan.
                const tl = gsap.timeline(
                    config.scroll
                        ? { scrollTrigger: { trigger: shape, start: SCROLL_START, once: true } }
                        : { delay: config.delay },
                );

                if (config.rotation) {
                    tl.from(svg, { rotation: config.rotation, duration: config.duration });
                }

                // Per pad een eigen from-stand, zodat de volgorde los staat van de stagger.
                const order = gsap.utils.distribute({
                    base: 0,
                    amount: config.stagger * (segments.length - 1),
                    from: config.order,
                    ease: "none",
                });

                segments.forEach((segment, i) => {
                    tl.fromTo(
                        segment,
                        getFromState(segment, i, svg, config),
                        { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1, duration: config.duration, ease: config.ease },
                        order(i, segment, segments),
                    );
                });

                return () => tl.kill();
            },
        );
    });
};

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initShapes, { once: true });
} else {
    initShapes();
}
